import { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import Toast from '../../components/ui/Toast';

const MAX_PORTFOLIO_IMAGES = 8;

const Portfolio = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [images, setImages] = useState([]);
  const [toast, setToast] = useState(null);

  // Load portfolio images
  useEffect(() => {
    if (user?.id) {
      loadImages();
    }
  }, [user?.id]);

  const loadImages = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('portfolio_images')
        .select('*')
        .eq('photographer_id', user.id)
        .order('is_featured', { ascending: false })
        .order('created_at', { ascending: false });

      if (error) throw error;
      setImages(data || []);
    } catch (error) {
      console.error('Failed to load images:', error);
      setToast({ type: 'error', message: 'فشل تحميل الصور' });
    } finally {
      setLoading(false);
    }
  };

  // Convert any image (including HEIC from iPhone) to JPEG via Canvas.
  // This ensures desktop browsers (Chrome/Firefox) can display the result.
  const convertToJpeg = (file) =>
    new Promise((resolve, reject) => {
      const url = URL.createObjectURL(file);
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement('canvas');
        canvas.width = img.naturalWidth;
        canvas.height = img.naturalHeight;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0);
        URL.revokeObjectURL(url);
        canvas.toBlob(
          (blob) => {
            if (blob) resolve(blob);
            else reject(new Error('فشل تحويل الصورة'));
          },
          'image/jpeg',
          0.95
        );
      };
      img.onerror = () => {
        URL.revokeObjectURL(url);
        reject(new Error('تعذّر قراءة الصورة'));
      };
      img.src = url;
    });

  // Upload new image
  const handleUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      setToast({ type: 'error', message: 'الملف المحدد ليس صورة' });
      return;
    }

    // Validate file size (max 10MB)
    if (file.size > 10 * 1024 * 1024) {
      setToast({ type: 'error', message: 'حجم الصورة كبير جداً (الحد الأقصى 10MB)' });
      return;
    }

    // Validate max portfolio images
    if (images.length >= MAX_PORTFOLIO_IMAGES) {
      setToast({ type: 'error', message: `وصلت للحد الأقصى (${MAX_PORTFOLIO_IMAGES} صور). احذف صورة لإضافة أخرى.` });
      return;
    }

    try {
      setUploading(true);
      setToast(null);

      // Convert to JPEG so all browsers (including desktop) can display it.
      // This also handles HEIC photos from iPhone.
      let uploadFile = file;
      try {
        const jpegBlob = await convertToJpeg(file);
        uploadFile = new File([jpegBlob], file.name.replace(/\.[^.]+$/, '.jpg'), { type: 'image/jpeg' });
      } catch (convErr) {
        console.warn('Image conversion failed, uploading original:', convErr);
      }

      // Generate unique filename (always .jpg after conversion)
      const timestamp = Date.now();
      const randomStr = Math.random().toString(36).substring(2, 9);
      const fileName = `${user.id}/${timestamp}-${randomStr}.jpg`;

      // Upload to storage
      const { data: uploadData, error: uploadError } = await supabase.storage
        .from('portfolios')
        .upload(fileName, uploadFile, {
          cacheControl: '3600',
          upsert: false
        });

      if (uploadError) {
        console.error('Upload error:', uploadError);
        if (uploadError.message?.includes('Bucket not found')) {
          throw new Error('مجلد الصور غير موجود. يرجى التواصل مع الإدارة.');
        }
        throw uploadError;
      }

      // Get public URL
      const { data: { publicUrl } } = supabase.storage
        .from('portfolios')
        .getPublicUrl(fileName);

      // Save to database
      const { error: insertError } = await supabase
        .from('portfolio_images')
        .insert({
          photographer_id: user.id,
          image_url: publicUrl,
          is_featured: images.length === 0, // First image is automatically featured
          created_at: new Date().toISOString()
        });

      if (insertError) throw insertError;

      setToast({ type: 'success', message: 'تم رفع الصورة بنجاح' });
      loadImages();
    } catch (error) {
      console.error('Failed to upload:', error);
      setToast({ type: 'error', message: error.message || 'فشل رفع الصورة' });
    } finally {
      setUploading(false);
      // Reset input
      event.target.value = '';
    }
  };

  // Set image as featured (cover image)
  const handleSetFeatured = async (imageId) => {
    try {
      // First, unset all featured images for this photographer
      await supabase
        .from('portfolio_images')
        .update({ is_featured: false })
        .eq('photographer_id', user.id);

      // Then set the selected image as featured
      const { error } = await supabase
        .from('portfolio_images')
        .update({ is_featured: true })
        .eq('id', imageId);

      if (error) throw error;

      setToast({ type: 'success', message: 'تم تعيين صورة الغلاف بنجاح' });
      loadImages();
    } catch (error) {
      console.error('Failed to set featured:', error);
      setToast({ type: 'error', message: 'فشل تعيين صورة الغلاف' });
    }
  };

  // Delete image
  const handleDelete = async (image) => {
    if (!confirm('هل أنت متأكد من حذف هذه الصورة؟')) return;

    try {
      // Extract file path from URL
      const urlParts = image.image_url.split('/portfolios/');
      const filePath = urlParts[urlParts.length - 1];

      // Delete from storage
      if (filePath) {
        await supabase.storage
          .from('portfolios')
          .remove([filePath]);
      }

      // Delete from database
      const { error } = await supabase
        .from('portfolio_images')
        .delete()
        .eq('id', image.id);

      if (error) throw error;

      // If deleted image was featured, set first remaining image as featured
      if (image.is_featured) {
        const remainingImages = images.filter(img => img.id !== image.id);
        if (remainingImages.length > 0) {
          await supabase
            .from('portfolio_images')
            .update({ is_featured: true })
            .eq('id', remainingImages[0].id);
        }
      }

      setToast({ type: 'success', message: 'تم حذف الصورة بنجاح' });
      loadImages();
    } catch (error) {
      console.error('Failed to delete:', error);
      setToast({ type: 'error', message: 'فشل حذف الصورة' });
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-2xl md:text-3xl font-bold text-slate-900 mb-2">
          معرض الأعمال
        </h1>
        <p className="text-slate-600">
          أضف صوراً من أعمالك السابقة. الصورة المثبتة ستظهر كغلاف في بطاقتك.
        </p>
      </div>

      {/* Upload Button */}
      <div className="mb-8">
        {images.length < MAX_PORTFOLIO_IMAGES ? (
          <label className="block">
            <input
              type="file"
              accept="image/*"
              onChange={handleUpload}
              disabled={uploading}
              className="hidden"
            />
            <div className="flex items-center justify-center gap-3 p-6 border-2 border-dashed border-slate-300 rounded-xl hover:border-primary hover:bg-primary/5 transition-colors cursor-pointer">
              {uploading ? (
                <>
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary"></div>
                  <span className="text-slate-600">جاري الرفع...</span>
                </>
              ) : (
                <>
                  <Icon name="Upload" size={24} className="text-slate-400" />
                  <span className="text-slate-600 font-medium">اضغط لرفع صورة جديدة</span>
                </>
              )}
            </div>
          </label>
        ) : (
          <div className="flex items-center justify-center gap-3 p-6 border-2 border-dashed border-slate-200 rounded-xl bg-slate-50">
            <Icon name="CheckCircle" size={24} className="text-slate-400" />
            <span className="text-slate-500 font-medium">وصلت للحد الأقصى ({MAX_PORTFOLIO_IMAGES} صور)</span>
          </div>
        )}
        <p className="text-xs text-slate-500 mt-2 text-center">
          {images.length}/{MAX_PORTFOLIO_IMAGES} صور · الحد الأقصى لحجم الصورة: 10MB · الصيغ المدعومة: JPG, PNG, WEBP
        </p>
      </div>

      {/* Images Grid */}
      {images.length === 0 ? (
        <div className="text-center py-16 bg-white rounded-2xl border border-slate-200">
          <Icon name="ImageOff" size={48} className="text-slate-300 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-slate-700 mb-2">لا توجد صور بعد</h3>
          <p className="text-slate-500">ابدأ برفع صور من أعمالك السابقة</p>
        </div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {images.map((image) => (
            <div
              key={image.id}
              className="relative group aspect-square rounded-2xl overflow-hidden bg-slate-100 ring-1 ring-slate-200 hover:ring-slate-300 transition-all duration-200"
            >
              <img
                src={image.image_url}
                alt="صورة من المعرض"
                className="w-full h-full object-cover"
              />

              {/* Delete button */}
              <button
                onClick={() => handleDelete(image)}
                title="حذف الصورة"
                className="absolute top-2 left-2 w-8 h-8 rounded-full bg-black/50 text-white flex items-center justify-center opacity-70 hover:opacity-100 hover:bg-red-500 hover:text-white transition-all duration-200 shadow-md"
              >
                <Icon name="Trash2" size={14} />
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Stats */}
      {images.length > 0 && (
        <div className="mt-8 p-4 bg-slate-50 rounded-xl text-center">
          <span className="text-slate-600">
            إجمالي الصور: <span className="font-bold text-slate-900">{images.length}</span> / {MAX_PORTFOLIO_IMAGES}
          </span>
        </div>
      )}

      {/* Toast */}
      {toast && (
        <Toast
          type={toast.type}
          message={toast.message}
          onClose={() => setToast(null)}
        />
      )}
    </div>
  );
};

export default Portfolio;
